import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BadgeCheck, Plus, Search, RefreshCw, ChevronLeft, ChevronRight,
  Pencil, Trash2, AlertCircle, X,
} from 'lucide-react';
import { policyApi, POLICY_TYPE_COLORS, POLICY_TYPE_LABELS } from '@/api/policy.api';
import type { PolicyListDto, PolicyFilterDto, CreatePolicyRequest } from '@/api/policy.api';
import { roleApi } from '@/api/role.api';
import type { RoleSummaryDto } from '@/types/role.types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AppRoutes } from '@/constants/routes';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';

// ── Zod Schema ────────────────────────────────────────────────────────────────
const schema = z.object({
  policyCode:       z.string().min(1, 'Policy code is required').max(20),
  policyName:       z.string().min(1, 'Policy name is required').max(100),
  appliesToRoleId:  z.string().optional().nullable(),
  appliesToUserId:  z.string().optional().nullable(),
  transactionType:  z.string().optional().nullable(),
  amountLimitFrom:  z.coerce.number().min(0).optional().nullable(),
  amountLimitTo:    z.coerce.number().min(0).optional().nullable(),
  currencyCode:     z.string().optional().nullable(),
  effectiveFrom:    z.string().optional().nullable(),
  effectiveTo:      z.string().optional().nullable(),
  isActive:         z.boolean(),
  remarks:          z.string().max(500).optional().nullable(),
});
type FormValues = z.infer<typeof schema>;

// ── Amount display helper ─────────────────────────────────────────────────────
const fmtAmount = (v?: number | null, currency?: string | null) =>
  v != null
    ? `${currency ?? ''} ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim()
    : '—';

// ── Form Modal ────────────────────────────────────────────────────────────────
interface FormModalProps {
  editId: string | null;
  onClose: () => void;
  onSaved: () => void;
}
const FormModal = ({ editId, onClose, onSaved }: FormModalProps) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleSummaryDto[]>([]);
  const isEdit = !!editId;

  useEffect(() => { roleApi.getList().then(r => setRoles(r.data.data ?? [])).catch(() => {}); }, []);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: { isActive: true, policyCode: '', policyName: '' },
    });

  useEffect(() => {
    if (!editId) { reset({ isActive: true, policyCode: '', policyName: '' }); return; }
    policyApi.getById(editId).then(res => {
      const d = res.data.data;
      if (!d) return;
      reset({
        policyCode:       d.policyCode,
        policyName:       d.policyName,
        appliesToRoleId:  d.appliesToRoleId  ?? null,
        appliesToUserId:  d.appliesToUserId  ?? null,
        transactionType:  d.transactionType  ?? null,
        amountLimitFrom:  d.amountLimitFrom  ?? null,
        amountLimitTo:    d.amountLimitTo    ?? null,
        currencyCode:     d.currencyCode     ?? null,
        effectiveFrom:    d.effectiveFrom    ? d.effectiveFrom.substring(0, 10) : null,
        effectiveTo:      d.effectiveTo      ? d.effectiveTo.substring(0, 10)   : null,
        isActive:         d.isActive,
        remarks:          d.remarks          ?? null,
      });
    }).catch(() => setApiError('Failed to load policy.'));
  }, [editId, reset]);

  const onSubmit = async (values: FormValues) => {
    setApiError(null);
    const payload: CreatePolicyRequest = {
      ...values,
      policyType:      'ApprovalLimit',
      appliesToRoleId: values.appliesToRoleId || null,
      appliesToUserId: values.appliesToUserId || null,
      transactionType: values.transactionType || null,
      amountLimitFrom: values.amountLimitFrom ?? null,
      amountLimitTo:   values.amountLimitTo   ?? null,
      currencyCode:    values.currencyCode    || null,
      effectiveFrom:   values.effectiveFrom   || null,
      effectiveTo:     values.effectiveTo     || null,
      remarks:         values.remarks         || null,
    };
    try {
      if (isEdit) await policyApi.update(editId!, payload);
      else        await policyApi.create(payload);
      onSaved();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setApiError(e?.response?.data?.message ?? 'Save failed.');
    }
  };

  const inp = 'w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-gray-800">
          <h2 className="font-semibold text-slate-800 dark:text-gray-200">{isEdit ? 'Edit' : 'New'} Approval Limit Policy</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {apiError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle size={14} /> {apiError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Policy Code *</label>
              <input {...register('policyCode')} className={inp} placeholder="e.g. APPR-LMT-001" />
              {errors.policyCode && <p className="text-red-500 text-xs mt-1">{errors.policyCode.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Currency</label>
              <input {...register('currencyCode')} className={inp} placeholder="e.g. USD" maxLength={3} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Policy Name *</label>
            <input {...register('policyName')} className={inp} placeholder="Descriptive name" />
            {errors.policyName && <p className="text-red-500 text-xs mt-1">{errors.policyName.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Transaction Type</label>
              <input {...register('transactionType')} className={inp} placeholder="e.g. GL_JOURNAL" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Applies To Role</label>
              <select {...register('appliesToRoleId')} className={inp}>
                <option value="">— Any role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Amount Limit From</label>
              <input {...register('amountLimitFrom')} type="number" step="0.01" className={inp} placeholder="0.00" />
              {errors.amountLimitFrom && <p className="text-red-500 text-xs mt-1">{errors.amountLimitFrom.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Amount Limit To</label>
              <input {...register('amountLimitTo')} type="number" step="0.01" className={inp} placeholder="999,999.99" />
              {errors.amountLimitTo && <p className="text-red-500 text-xs mt-1">{errors.amountLimitTo.message}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Effective From</label>
              <input {...register('effectiveFrom')} type="date" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Effective To</label>
              <input {...register('effectiveTo')} type="date" className={inp} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Remarks</label>
            <textarea {...register('remarks')} rows={2} className={inp} placeholder="Optional notes" />
          </div>
          <div className="flex items-center gap-2">
            <input {...register('isActive')} type="checkbox" id="al-active" className="accent-blue-600" />
            <label htmlFor="al-active" className="text-sm text-slate-700 dark:text-gray-300">Active</label>
          </div>
        </form>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 dark:border-gray-800">
          <button type="button" onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-slate-800/60">
            Cancel
          </button>
          <button type="submit" form="approval-form" disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
            {isSubmitting ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export const ApprovalLimitsPage = () => {
  const navigate  = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.ADMIN_POLICY_CREATE);
  const canDelete = hasPermission(Permissions.ADMIN_POLICY_DELETE);

  const [rows,       setRows]       = useState<PolicyListDto[]>([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [pageSize]                  = useState(10);
  const [editId,     setEditId]     = useState<string | null>(null);
  const [showForm,   setShowForm]   = useState(false);
  const [deleteId,   setDeleteId]   = useState<string | null>(null);
  const [deleteErr,  setDeleteErr]  = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filter: PolicyFilterDto = {
        searchTerm: search || undefined,
        policyType: 'ApprovalLimit',
        pageNumber: page,
        pageSize,
      };
      const res = await policyApi.getList(filter);
      const data = res.data.data;
      setRows(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await policyApi.delete(deleteId);
      setDeleteId(null);
      load();
    } catch {
      setDeleteErr('Failed to delete policy.');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const badge = POLICY_TYPE_COLORS['ApprovalLimit'];
  const label = POLICY_TYPE_LABELS['ApprovalLimit'];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(AppRoutes.ADMIN_POLICY_SETUP)}
          className="text-slate-400 hover:text-slate-600">
          <ChevronLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
            <BadgeCheck size={18} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-gray-200">Approval Limits</h1>
            <p className="text-xs text-slate-500 dark:text-gray-400">Amount-based approval threshold policies</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {canCreate && (
            <button
              onClick={() => { setEditId(null); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
            >
              <Plus size={16} /> New Limit
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search policies…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${badge}`}>{label}</span>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-gray-800 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-gray-800">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Code</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Transaction Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Amount Range</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Applies To</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400 dark:text-gray-500 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400 dark:text-gray-500 text-sm">No approval limit policies found.</td></tr>
            ) : rows.map(row => (
              <tr key={row.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/60 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-gray-300">{row.policyCode}</td>
                <td className="px-4 py-3 text-slate-800 dark:text-gray-200 font-medium">{row.policyName}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-gray-400 text-xs">{row.transactionType ?? '—'}</td>
                <td className="px-4 py-3 text-slate-600 dark:text-gray-400 text-xs whitespace-nowrap">
                  {fmtAmount(row.amountLimitFrom, row.currencyCode)}
                  {' – '}
                  {fmtAmount(row.amountLimitTo, row.currencyCode)}
                </td>
                <td className="px-4 py-3 text-slate-500 dark:text-gray-400 text-xs">
                  {row.appliesToRoleName ?? row.appliesToUserName ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    row.isActive ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400'
                  }`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => { setEditId(row.id); setShowForm(true); }}
                      className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Edit">
                      <Pencil size={13} />
                    </button>
                    {canDelete && (
                      <button onClick={() => { setDeleteId(row.id); setDeleteErr(null); }}
                        className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-slate-900">
            <p className="text-xs text-slate-500 dark:text-gray-400">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-40">
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs text-slate-600 dark:text-gray-400 px-2">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-40">
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <FormModal
          editId={editId}
          onClose={() => { setShowForm(false); setEditId(null); }}
          onSaved={() => { setShowForm(false); setEditId(null); load(); }}
        />
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-slate-800 dark:text-gray-200 mb-2">Delete Policy?</h3>
            <p className="text-slate-500 dark:text-gray-400 text-sm mb-4">This action cannot be undone.</p>
            {deleteErr && <p className="text-red-500 text-xs mb-3">{deleteErr}</p>}
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 py-2 border border-slate-200 dark:border-gray-700 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-slate-800/60">
                Cancel
              </button>
              <button onClick={handleDelete}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
